import {
  PlaygroundLocalMsg,
  usePlaygroundLocalMsg,
} from "@faire/prototype-playground/platform/localization";
import { Button } from "@faire/slate/components/button";
import { Divider } from "@faire/slate/components/divider";
import { Toast, ToastContainer } from "@faire/slate/components/toast";
import { ClipboardCheck } from "@faire/slate-icons/ClipboardCheck";
import { useRef } from "react";

import { RetailerMobileNav } from "./RetailerMobileNav";
import {
  getReturnRequestItemCount,
  RETURN_REQUEST_BRAND_NAME,
  RETURN_REQUEST_RETURN_BY,
  type ReturnRequestProduct,
} from "./returnRequestData";

const LABEL_TOAST_DISMISS_MS = 1200;

type ReturnRequestReceivedScreenProps = {
  products: ReturnRequestProduct[];
  onDone: () => void;
};

export const ReturnRequestReceivedScreen = ({
  products,
  onDone,
}: ReturnRequestReceivedScreenProps) => {
  const toastTimeoutRef = useRef<number | null>(null);
  const itemCount = getReturnRequestItemCount(products);
  const printLabel = usePlaygroundLocalMsg(
    "Print shipping label for this return"
  );
  const doneLabel = usePlaygroundLocalMsg("Done");
  const labelDownloadedMessage = usePlaygroundLocalMsg("Label downloaded.");

  const handlePrintLabel = () => {
    if (toastTimeoutRef.current !== null) {
      window.clearTimeout(toastTimeoutRef.current);
    }

    const toastId = Toast.create({
      description: labelDownloadedMessage,
    });
    toastTimeoutRef.current = window.setTimeout(() => {
      Toast.dismiss(toastId);
      toastTimeoutRef.current = null;
    }, LABEL_TOAST_DISMISS_MS);
  };

  return (
    <div className="bg-fs-surface-primary flex min-h-full flex-col">
      <ToastContainer />
      <RetailerMobileNav
        showProgress={false}
        showBack={false}
        showClose
        onClose={onDone}
      />
      <div className="flex flex-1 flex-col gap-(--slate-dimensions-6) px-(--slate-spacing-section-medium) pt-(--slate-spacing-section-medium) pb-(--slate-spacing-section-large)">
        <div className="flex flex-col gap-(--slate-dimensions-2)">
          <ClipboardCheck className="text-fs-text-primary size-10" />
          <h1 className="type-fs-page-heading text-fs-text-primary">
            <PlaygroundLocalMsg
              defaultMessage="Report received. Return affected items by {date}."
              values={{ date: RETURN_REQUEST_RETURN_BY }}
            />
          </h1>
        </div>

        <div className="flex flex-col gap-(--slate-dimensions-2)">
          <span className="type-fs-paragraph-medium text-fs-text-primary">
            <PlaygroundLocalMsg defaultMessage="Your replacement order:" />
          </span>
          <p className="type-fs-paragraph text-fs-text-primary">
            <PlaygroundLocalMsg defaultMessage="Your request for a replacement has been sent to the brand." />
          </p>
        </div>

        <Divider />

        <div className="flex flex-col gap-(--slate-dimensions-6)">
          <div className="flex flex-col gap-(--slate-dimensions-2)">
            <span className="type-fs-paragraph-medium text-fs-text-primary">
              <PlaygroundLocalMsg defaultMessage="Return instructions:" />
            </span>
            <ol className="type-fs-paragraph text-fs-text-primary flex list-decimal flex-col gap-(--slate-dimensions-2) ps-[21px]">
              <li>
                <PlaygroundLocalMsg defaultMessage="Pack the items on the packing slip using the original packaging. Include the packing slip in the box." />
              </li>
              <li>
                <PlaygroundLocalMsg
                  defaultMessage="Print the pre-paid return label and attach it to the box. Shipping is paid by {brand}."
                  values={{ brand: RETURN_REQUEST_BRAND_NAME }}
                />
              </li>
              <li>
                <PlaygroundLocalMsg defaultMessage="Drop off at any FedEx location, or schedule a pickup." />
              </li>
              <li>
                <PlaygroundLocalMsg defaultMessage="Once your package is scanned, your refund will be processed." />
              </li>
            </ol>
          </div>

          <div className="flex flex-col gap-(--slate-spacing-element-medium)">
            <div className="flex flex-col gap-(--slate-dimensions-1)">
              <span className="type-fs-paragraph-medium text-fs-text-primary">
                <PlaygroundLocalMsg defaultMessage="Returns requested:" />
              </span>
              <span className="type-fs-paragraph text-fs-text-subdued">
                <PlaygroundLocalMsg
                  defaultMessage="{count} items"
                  values={{ count: itemCount }}
                />
              </span>
            </div>
            <div className="flex flex-col gap-(--slate-spacing-element-medium)">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="flex gap-(--slate-spacing-element-medium)"
                >
                  <div className="bg-fs-surface-tertiary size-[72px] shrink-0 overflow-hidden rounded-[2px]">
                    <img
                      src={product.imageUrl}
                      alt=""
                      className="size-full object-cover"
                    />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="type-fs-paragraph-medium text-fs-text-primary">
                      {product.name}
                    </span>
                    <span className="type-fs-paragraph text-fs-text-primary">
                      <PlaygroundLocalMsg
                        defaultMessage="Quantity: {count}"
                        values={{ count: product.quantity }}
                      />
                    </span>
                    <span className="type-fs-paragraph text-fs-text-primary">
                      {product.color}, {product.size}
                    </span>
                    <span className="type-fs-label-medium text-fs-message-text-critical">
                      {product.receivedIssueLabel}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-fs-surface-primary border-fs-border-muted sticky bottom-0 flex flex-col gap-(--slate-dimensions-2) border-t px-(--slate-spacing-section-medium) pt-(--slate-dimensions-4) pb-(--slate-spacing-section-medium)">
        <Button variant="primary" fullWidth onClick={handlePrintLabel}>
          {printLabel}
        </Button>
        <Button variant="secondary" fullWidth onClick={onDone}>
          {doneLabel}
        </Button>
      </div>
    </div>
  );
};
